import { Container, IDestroyOptions, Sprite } from "pixi.js";
import { MapZipCodeDescription } from "../types/MapZipCodeDescription";
import { gsap } from "gsap";
import { IMarketplaceMapModel } from "../models/map/IMarketplaceMapModel";
import { Event } from "event-system";
import { MapFilter } from "../models/map/MapFilter";
import { IMarketplaceController } from "../models/controller/IMarketplaceController";
import { IPlayerModel } from "../../../../common/models/IPlayerModel";
import { DefaultMarketplacePin } from "./pins/DefaultMarketplacePin";
import { MyMarketplacePin } from "./pins/MyMarketplacePin";
import { IBuildingModel } from "../../../../common/models/Building/IBuildingModel";
import { MarketplaceResourcePackage } from "../../resources/MarketplaceResourcePackage";

export class MapZipCode extends Container {
    public static readonly DEFAULT_SCALE = 0.44;

    public readonly onZipCodePinClick: Event;
    private readonly marketplaceResources: MarketplaceResourcePackage;
    private readonly mapZipCodeDescription: MapZipCodeDescription;
    private readonly marketplaceController: IMarketplaceController;
    private readonly marketplaceMapModel: IMarketplaceMapModel;
    private readonly playerModel: IPlayerModel;
    private mapZoneId: number;
    private zipCodePinsContainer: Container;
    private selectedSprite: Sprite;
    private corner: Sprite;
    private pin: DefaultMarketplacePin;
    private myPin: MyMarketplacePin;
    private isSelected: boolean;
    private selectAnimation: gsap.core.Timeline;

    public constructor(
        marketplaceResources: MarketplaceResourcePackage,
        marketplaceController: IMarketplaceController,
        marketplaceMapModel: IMarketplaceMapModel,
        playerModel: IPlayerModel,
        mapZipCodeDescription: MapZipCodeDescription,
        mapZoneId: number,
        zipCodePinsContainer: Container
    ) {
        super();
        this.onZipCodePinClick = new Event();
        this.marketplaceResources = marketplaceResources;
        this.marketplaceController = marketplaceController;
        this.marketplaceMapModel = marketplaceMapModel;
        this.playerModel = playerModel;
        this.mapZipCodeDescription = mapZipCodeDescription;
        this.mapZoneId = mapZoneId;
        this.zipCodePinsContainer = zipCodePinsContainer;
        this.isSelected = false;
        this.selectAnimation = new gsap.core.Timeline();
        this.build();
        this.once("added", this.addedToStage.bind(this));
    }

    private build(): void {
        this.corner = new Sprite(this.mapZipCodeDescription.cornerTexture);
        this.addChild(this.corner);

        this.selectedSprite = new Sprite(this.mapZipCodeDescription.selectedTexture);
        this.selectedSprite.position = this.mapZipCodeDescription.selectedGlowPosition;
        this.addChild(this.selectedSprite);

        this.pin = new DefaultMarketplacePin(this.marketplaceResources, 10);
        this.pin.anchor.set(0.5);
        this.pin.position = this.mapZipCodeDescription.otherPinPosition;
        this.pin.eventMode = "static";
        this.pin.on("pointerdown", () => this.onZipCodePinClick.emit());
        this.zipCodePinsContainer.addChild(this.pin);
    }

    private buildMyPin(): void {
        this.myPin = new MyMarketplacePin(this.marketplaceResources);
        this.myPin.anchor.set(0.5);
        this.myPin.position = this.mapZipCodeDescription.myPinPosition;
        this.myPin.eventMode = "static";
        this.myPin.on("pointerdown", () => this.onZipCodePinClick.emit());
        this.zipCodePinsContainer.addChild(this.myPin);
    }

    private addedToStage(): void {
        this.selectedSprite.alpha = 0;
        this.updatePinsState(true);
    }

    public updatePinsState(fromFirstShow: boolean = false): void {
        const zipCodes = this.marketplaceMapModel.zipCodes.value;
        const currentZipCode = zipCodes.find(zipCode => zipCode.zipCode.toString() === this.mapZipCodeDescription.zipCode);
        this.pin.updateBuildingsAmount(currentZipCode?.buildingsAmount ?? 0);
        this.pin.visible = currentZipCode != null && currentZipCode.buildingsAmount > 0;

        const myBuildingOnMp = this.getMyBuildingOnMp();
        if (myBuildingOnMp != null) {
            if (fromFirstShow) {
                this.buildMyPin();
                this.myPin.updateNftTexture(myBuildingOnMp.imageUrl.value);
            }
        }
        else {
            this.myPin?.destroy();
        }
    }

    private getMyBuildingOnMp(): IBuildingModel {
        return this.playerModel.buildings.value.find(building => building.zipCode.value === this.mapZipCodeDescription.zipCode && building.marketplaceId.value != null);
    }

    public async selectedHandle(isSelectedNewValue: boolean): Promise<void> {
        if (!this.isSelected && isSelectedNewValue) {
            this.marketplaceController.updateMapFilter(new MapFilter(this.mapZoneId, this.mapZipCodeDescription.zipCode));
        }
        this.isSelected = isSelectedNewValue;
        const alpha = isSelectedNewValue ? 1 : 0;
        this.selectAnimation.to(this.selectedSprite, { alpha: alpha, duration: 0.5 });
        await this.selectAnimation.play();
        this.selectAnimation.clear();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.pin.off("pointerdown");
        this.myPin?.off("pointerdown");
        this.selectAnimation?.kill();
        super.destroy(options);
    }
}
